// DS1.7.12
import React from 'react';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useTranslation } from 'react-i18next';
import TextTooltipOnHover from '../../Popover/Preset/TextTooltipOnHover';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import s from './TierStateBadges.scss';

export interface Props {
  deviation: PapiDeviation;
  showNeedCoreState?: boolean;
  showPendingDeletionState?: boolean;
  className?: string;
  badgeClassName?: string;
  withTooltip?: boolean;
}

const TierStateBadges: React.FC<Props> = ({
  deviation,
  showNeedCoreState,
  showPendingDeletionState,
  className,
  badgeClassName: badgeClassNameProp,
  withTooltip = true,
}) => {
  const { t } = useTranslation();
  const { isOwner, isUserSubscribed } = useSubscriptionTier(deviation);
  const canPublish = deviation.tier?.canUserPublish;
  const needCoreToPublish = isOwner && deviation.tier?.state === 'draft' && !canPublish; // currently the only reason the owner cant publish

  let label;
  let tooltipText;
  let badgeClassName;

  if (deviation.tier?.state === 'draft') {
    tooltipText = t('deviationview.tierCard.stateBadge.tooltip.draft');
    label = t('deviationview.tierCard.stateBadge.label.draft');
    badgeClassName = 'draft';
  } else if (deviation.tier?.state === 'pending_deletion' && showPendingDeletionState) {
    label = t('deviationview.tierCard.stateBadge.label.pendingDeletion');
    tooltipText = t('deviationview.tierCard.stateBadge.tooltip.pendingDeletion.visitor');
    if (isOwner) {
      tooltipText = t('deviationview.tierCard.stateBadge.tooltip.pendingDeletion.owner');
    } else if (isUserSubscribed) {
      tooltipText = t('deviationview.tierCard.stateBadge.tooltip.pendingDeletion.subscriber');
    }
    badgeClassName = 'pending-deletion';
  }

  if (!label) {
    // because typescript is not always smart enough
    return null;
  }

  const badge = <div className={c(s['badge'], s[badgeClassName], badgeClassNameProp)}>{label}</div>;

  const coreBadge = showNeedCoreState && needCoreToPublish && (
    <div className={c(s['badge'], s['need-core'])}>
      {t('deviationview.tierCard.stateBadge.label.needCore')}
    </div>
  );

  return (
    <div className={c(s['root'], className)}>
      {withTooltip ? (
        <TextTooltipOnHover tooltipText={tooltipText}>{badge}</TextTooltipOnHover>
      ) : (
        badge
      )}

      {withTooltip && coreBadge ? (
        <TextTooltipOnHover tooltipText={t('deviationview.tierCard.stateBadge.tooltip.needCore')}>
          {coreBadge}
        </TextTooltipOnHover>
      ) : (
        coreBadge
      )}
    </div>
  );
};
TierStateBadges.displayName = 'TierStateBadges';

export default TierStateBadges;
